import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';

import { useAnswerSaves } from '../../contexts/answer-saves';
import { useLanguage } from '../../contexts/language';
import { useAttempt } from '../../hooks/attempt';

import { LoadFailed } from '../load-failed';
import { TestCheck } from '../test-check';
import { TestItem } from '../test-item';

import styles from './style.module.css';

// Taking a questionnaire: one item per screen, at /tests/<attempt>/<item>,
// then the check at /tests/<attempt>/check. Every answer is saved the moment
// it is chosen, so Save and exit is only a way out and nothing is lost.
//
// A submitted attempt only has its check, read-only; an attempt that is not
// there for this patient, such as somebody else's, goes back to Tests. A read
// that failed for any other reason offers to try again instead.
export function TestAttempt() {
  const { text } = useLanguage();
  const navigate = useNavigate();
  const { attemptId, step } = useParams();
  const { attempt, error, retry, reload } = useAttempt(attemptId);
  const { savesOf } = useAnswerSaves();
  const position = Number(step);
  const saves = savesOf(attemptId);
  const [ progress, setProgress ] = useState('current');
  const reads = useRef(0);

  // Reads the attempt again, so the counter, the progress bar and the check
  // show what the service now holds. A read that fails leaves the saved
  // answers saved and the screens showing what was read before: progress is
  // then 'failed', and the screens offer to read again (#123). Only the
  // newest read decides, so an older one settling late cannot undo it.
  const readProgress = useCallback(function() {
    reads.current = reads.current + 1;

    const read = reads.current;

    setProgress('reading');

    return reload().then(function() {
      if (read === reads.current) {
        setProgress('current');
      }
    }).catch(function() {
      if (read === reads.current) {
        setProgress('failed');
      }
    });
  }, [ reload ]);

  // Another attempt starts with current progress: whatever was being read or
  // had failed for the previous one no longer applies to it (#126).
  useEffect(function() {
    reads.current = reads.current + 1;
    setProgress('current');
  }, [ attemptId ]);

  // Each saved answer is read back.
  useEffect(function() {
    if (saves > 0) {
      readProgress();
    }
  }, [ saves, readProgress ]);

  // The first item's Back leaves for Tests, like Save and exit.
  const onBack = useCallback(function() {
    if (position === 1) {
      return navigate('/tests');
    }

    return navigate(`/tests/${ attemptId }/${ position - 1 }`);
  }, [ attemptId, position, navigate ]);

  // The last item's Next leads to the check.
  const onNext = useCallback(function() {
    if (position === attempt.questionnaire.items.length) {
      return navigate(`/tests/${ attemptId }/check`);
    }

    return navigate(`/tests/${ attemptId }/${ position + 1 }`);
  }, [ attempt, attemptId, position, navigate ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (attempt === undefined) {
    return null;
  }

  if (attempt === null) {
    return <Navigate to='/tests' replace/>;
  }

  if (attempt.status === 'SUBMITTED' && step !== 'check') {
    return <Navigate to={ `/tests/${ attemptId }/check` } replace/>;
  }

  if (step === 'check') {
    return <TestCheck attempt={ attempt } progress={ progress } onReadProgress={ readProgress }/>;
  }

  const { questionnaire, answers } = attempt;
  const total = questionnaire.items.length;

  if (Number.isInteger(position) === false || position < 1 || position > total) {
    return <Navigate to={ `/tests/${ attemptId }/1` } replace/>;
  }

  const answer = answers.find(function(saved) {
    return saved.position === position;
  });

  // The choice the item opens with: the saved answer, or none yet.
  let chosen = '';

  if (answer != null) {
    chosen = String(answer.value);
  }

  let progressFailed = null;

  if (progress === 'failed') {
    progressFailed = <LoadFailed title={ text.progressFailed } onRetry={ readProgress }/>;
  }

  let saved = null;

  if (answers.length > 0) {
    saved = <span className={ styles.saved }>{ text.draftSaved }</span>;
  }

  return (
    <section className={ styles.attempt }>
      <div className={ styles.top }>
        <Link className={ styles.exit } to='/tests'>
          <span className={ styles.arrow } aria-hidden='true'></span>
          { text.saveAndExit }
        </Link>

        { saved }
      </div>

      <div className={ styles.status }>
        <span className={ styles.name }>{ questionnaire.name }</span>

        <div className={ styles.counter }>
          <span>{ text.questionOf.replace('{position}', position).replace('{total}', total) }</span>
          <span>{ text.answeredCount.replace('{answered}', answers.length) }</span>
        </div>

        <div className={ styles.bar } role='progressbar' aria-label={ text.answered } aria-valuemin='0' aria-valuemax={ total } aria-valuenow={ answers.length }>
          <div className={ styles.fill } style={ { '--progress': `${ answers.length / total * 100 }%` } }></div>
        </div>
      </div>

      { progressFailed }

      <TestItem
        key={ position }
        attemptId={ attemptId }
        questionnaire={ questionnaire }
        position={ position }
        chosen={ chosen }
        onBack={ onBack }
        onNext={ onNext }
      />

      <p className={ styles.note }>{ text.draftNote }</p>
    </section>
  );
}
